/**
 * PATH: packages/ui/src/security/sanitizeHtml.ts
 * FILE_ID: UI-SEC-UTIL-001
 * PURPOSE: Sanitize untrusted HTML strings before injecting via
 *          `dangerouslySetInnerHTML` so XSS payloads in email
 *          bodies / kanban cards / mentions cannot execute. Also
 *          rewrites `<img src="cid:...">` references in email
 *          bodies to signed inline-image endpoints so the email's
 *          visual narrative is preserved.
 * WHY: Multiple hubs (email, crm, steel, qa) render raw `body_html`
 *      from external mailboxes. A crafted email with `<script>` or
 *      `<img onerror=...>` would execute in the operator's browser
 *      session. Equally, before this rewrite existed every inline
 *      image was being stripped — operator complaint 2026-05-09:
 *      "image placements inline of the emails still stands.. this
 *      is very important as it crafts a story". The cid → inline_url
 *      rewrite resolves the references to a signed backend endpoint
 *      that streams the bytes (see
 *      `organisation/services/email_inline_signer.py` and
 *      `organisation/views/email_views/inline_attachment_views.py`).
 * FLOW:
 *   ┌────────────┐    ┌───────────────┐    ┌────────────┐
 *   │ raw HTML   │ →  │ cid: rewrite  │ →  │ DOMPurify  │ → safe HTML
 *   │ (email,    │    │ via cid→url   │    │ (default   │
 *   │  user)     │    │ map           │    │  cfg)      │
 *   └────────────┘    └───────────────┘    └────────────┘
 * DEPENDENCIES:
 *   - dompurify: industry-standard HTML sanitizer.
 * RELATED:
 *   - apps/sam/src/features/email/components/EmailReader.tsx
 *   - apps/sam/src/features/bills/components/BillEmailTab.tsx
 *   - apps/email/src/pages/EmailViewPage.tsx
 *   - apps/steel/src/features/email/EmailHubPage.tsx
 *   - apps/crm/src/components/quotes/email/EmailDetailPanel.tsx
 *   - packages/bills/src/components/BillEmailPanel.tsx
 */
import DOMPurify from 'dompurify'

import { unwrapWordStyleComments } from './wordEmailCss'

/** Minimal shape we read off each attachment to resolve cid: refs.
 *  Anything more is fine; the helper just ignores extra fields. */
export interface InlineAttachmentRef {
  id: number | string
  content_id?: string | null
  inline_url?: string | null
  is_inline?: boolean | null
  content_type?: string | null
}

const CID_IMG_PATTERN =
  /<img\b([^>]*?)\bsrc\s*=\s*["']cid:([^"']*)["']([^>]*)>/gi

/** Lower-case + strip surrounding angle brackets so a user-typed
 *  `<img src="cid:image001@01">` matches the backend's stored
 *  `image001@01` even if either side has stray whitespace or
 *  `<...>` wrappers. */
function normaliseCid(raw: string | null | undefined): string {
  if (!raw) return ''
  let out = raw.trim()
  if (out.startsWith('<') && out.endsWith('>')) out = out.slice(1, -1)
  return out.toLowerCase()
}

/** A signed URL expiring within this window will be dead by the time the
 *  browser's request lands, so treat it as already expired. */
const SIGNED_URL_EXP_SKEW_MS = 5_000

/**
 * Is this a signed URL whose ``exp`` (unix seconds) has already passed?
 *
 * WHY (2026-07-24, "this api is constantly getting called with the error"):
 * inline-image URLs are signed with a 15-minute TTL. Once the signature
 * lapses, every re-render that rebuilds the body DOM re-issues the SAME
 * dead URL and the backend 410s it — an email kept open past the TTL was
 * hammering `/inline/` in bursts on every poll-driven re-render, forever.
 * The expiry is right there in the querystring, so the renderer can simply
 * REFUSE to emit a URL it knows is dead: the ref falls to the calm
 * "Image syncing…" placeholder instead (zero network), and the existing
 * bounded refetch machinery restores a freshly-signed URL when it can.
 *
 * URLs without an ``exp`` param are never treated as expired.
 */
export function isExpiredSignedUrl(url: string, nowMs: number = Date.now()): boolean {
  const m = /[?&]exp=(\d+)(?:&|$)/.exec(url)
  if (!m) return false
  const expMs = Number(m[1]) * 1000
  return Number.isFinite(expMs) && expMs <= nowMs + SIGNED_URL_EXP_SKEW_MS
}

/** Build a `cid → inline_url` map from the attachment list. We accept
 *  any shape that includes `content_id` + `inline_url`; all other
 *  fields are optional. Inline URLs whose signature has ALREADY expired
 *  are excluded — emitting one guarantees a 410, so the ref is better
 *  served by the syncing placeholder (see isExpiredSignedUrl). */
function buildCidMap(
  attachments: ReadonlyArray<InlineAttachmentRef> | null | undefined,
): Map<string, string> {
  const out = new Map<string, string>()
  if (!attachments) return out
  for (const att of attachments) {
    const cid = normaliseCid(att.content_id)
    if (cid && att.inline_url && !isExpiredSignedUrl(att.inline_url)) {
      out.set(cid, att.inline_url)
    }
  }
  return out
}

/** Attribute the canonical email-body container stamps on itself —
 *  every selector inside a sender-supplied `<style>` block gets scoped
 *  under this attribute (see `scopeEmailCss`) so a marketing email's
 *  `p { color: white }` can't restyle the whole app. */
export const EMAIL_BODY_SCOPE_ATTR = 'data-email-body-scope'
const EMAIL_BODY_SCOPE_SELECTOR = `[${EMAIL_BODY_SCOPE_ATTR}]`

/** Add privacy + perf attributes to every `<img>` element in the
 *  sanitised DOM:
 *
 *    * ``referrerpolicy="no-referrer"`` — stops the browser sending
 *      our `Referer` (which leaks the operator's tenant URL + email
 *      id to the sender's tracking server when a marketing-email
 *      tracking pixel loads). External `<img>` only — relative +
 *      signed inline URLs go to our own backend.
 *    * ``loading="lazy"`` — defers fetches for images below the fold.
 *    * ``decoding="async"`` — keeps painting while bytes decode.
 *
 *  BUG-2026-06-11-IMG-REGEX-CORRUPTION: this used to be two regex
 *  passes over the serialised HTML, which silently mangled `<img>`
 *  tags containing `>` inside attribute values (alt text, data URIs)
 *  and could double-append hints. Operating on the sanitised DOM is
 *  exact — no parsing heuristics, idempotent by construction. */
function hardenImageElements(root: ParentNode): void {
  for (const el of Array.from(root.querySelectorAll('img'))) {
    if (!el.hasAttribute('loading')) el.setAttribute('loading', 'lazy')
    if (!el.hasAttribute('decoding')) el.setAttribute('decoding', 'async')
    const src = el.getAttribute('src') || ''
    if (/^(?:https?:)?\/\//i.test(src) && !el.hasAttribute('referrerpolicy')) {
      el.setAttribute('referrerpolicy', 'no-referrer')
    }
    preserveHeightOnlyImage(el)
  }
}

/** BUG-2026-06-22-EMAIL-SIGNATURE-IMG-BLOWN-UP (operator: "the image is
 *  fucked in the email on the signature").
 *
 *  Tailwind preflight ships a GLOBAL `img,video{max-width:100%;height:auto}`
 *  (the responsive-image / CLS default). Because that is an author *rule*, it
 *  outranks the presentational `height="N"` *attribute* that email signatures
 *  use to size logos + social icons — a height-only `<img height="24">` (no
 *  width; the Apple-Mail / signature-generator idiom) loses its 24px and
 *  balloons to its intrinsic pixel size (verified in a real browser against
 *  the deployed CSS: a 200×200 asset with `height="40"` rendered 200×200).
 *  A CSS-class refinement can't fix this — preflight's element rule still
 *  wins over the attribute. The only thing that beats it without disabling
 *  the responsive default is an INLINE style (specificity 1,0,0,0).
 *
 *  So: when an image declares a height attribute but NO width attribute (and
 *  no inline height already), mirror that height into the inline style. The
 *  browser then derives width from the intrinsic aspect ratio at that height
 *  (capped by the inherited `max-width:100%`) — the author's intended size,
 *  no distortion. Width-bearing images are deliberately left untouched: with
 *  both attributes the UA's `aspect-ratio: attr(width)/attr(height)` rule +
 *  `max-width:100%` already scale them proportionally (verified: a 1200×300
 *  `<img width=1200 height=300>` rendered 600×150 in a 600px column), and
 *  pinning their height inline would BREAK that proportional shrink. */
function preserveHeightOnlyImage(el: HTMLImageElement): void {
  if (el.hasAttribute('width')) return
  if (el.style.height) return // author already set an explicit inline height
  const h = (el.getAttribute('height') || '').trim()
  if (!h) return
  // The HTML height attribute is a pixel integer (modern spec); tolerate an
  // explicit unit too. Anything exotic is ignored (left to the UA default).
  const cssHeight = /^\d+$/.test(h)
    ? `${h}px`
    : /^\d+(?:\.\d+)?(?:px|%|em|rem|vh|vw)$/i.test(h)
      ? h
      : ''
  if (!cssHeight) return
  el.style.height = cssHeight
}

/** Force every navigable link in an email body to open in a new tab,
 *  isolated from the app.
 *
 *    * ``target="_blank"`` — click-throughs (newsletter CTAs,
 *      "Upgrade to paid", unsubscribe links, etc.) open in a new
 *      browser tab instead of navigating the operator AWAY from the
 *      inbox SPA. Same-tab navigation would blow away their session +
 *      scroll position and, for `#`-style links, hijack the app's own
 *      router. Operator expectation: "click-throughs in email should
 *      always open in a new tab".
 *    * ``rel="noopener noreferrer"`` — ``noopener`` severs the new
 *      tab's ``window.opener`` handle so a malicious landing page can't
 *      reverse-tabnab us (``window.opener.location = <phishing clone>``).
 *      ``noreferrer`` also drops the ``Referer`` so the destination
 *      can't read the tenant URL + email id — same privacy stance as
 *      the ``<img>`` referrer policy above.
 *
 *  Pure in-page fragment links (``href="#..."``) and href-less anchors
 *  are skipped — there is nothing to open, so forcing a blank tab would
 *  just spawn an empty window. Dangerous URL schemes (`javascript:`)
 *  are already stripped by DOMPurify before this runs. Operates on the
 *  sanitised DOM (post-DOMPurify) so it is exact + idempotent, matching
 *  the img/style passes. */
function hardenAnchorElements(root: ParentNode): void {
  for (const el of Array.from(root.querySelectorAll('a'))) {
    const href = (el.getAttribute('href') || '').trim()
    if (!href || href.startsWith('#')) continue
    el.setAttribute('target', '_blank')
    el.setAttribute('rel', 'noopener noreferrer')
  }
}

/** Prefix every selector in a sender-supplied CSS block with the
 *  email-body scope so the rules can ONLY match inside the rendered
 *  body container.
 *
 *  BUG-2026-06-11-EMAIL-STYLE-LEAK: DOMPurify allows `<style>` tags
 *  through by default, so a marketing email shipping
 *  `p { margin: 0 !important; color: #fff }` restyled every paragraph
 *  in the app shell for as long as the email was open. Gmail solves
 *  this by rewriting selectors — same approach here.
 *
 *  Handling:
 *    - plain rules         → `SCOPE <sel>` for each comma-part;
 *    - `body` / `html`     → mapped to the scope element itself;
 *    - @media/@supports/
 *      @layer/@container   → recurse into the block;
 *    - @font-face/
 *      @keyframes          → left as-is (no selectors to leak);
 *    - @import/@charset/
 *      @namespace          → dropped (external CSS fetch = tracking
 *                            vector and un-scopable).
 *
 *  A tokenizer-grade CSS parser isn't warranted here — brace counting
 *  covers real-world email CSS; pathological payloads degrade to
 *  inert (mis-scoped) rules, never to leakage beyond the body. */
export function scopeEmailCss(css: string, scope = EMAIL_BODY_SCOPE_SELECTOR): string {
  let out = ''
  let i = 0
  while (i < css.length) {
    const braceIdx = css.indexOf('{', i)
    if (braceIdx === -1) {
      // Trailing statement-only content (e.g. a dangling @import) —
      // at-statements are dropped, anything else is inert without a
      // block so it's safe to drop too.
      break
    }
    let prelude = css.slice(i, braceIdx)
    // Drop block-less at-statements that may precede the selector.
    prelude = prelude.replace(/@(?:import|charset|namespace)\b[^;]*;/gi, '')
    // Find the matching closing brace (handles nested @media blocks).
    let depth = 1
    let j = braceIdx + 1
    while (j < css.length && depth > 0) {
      if (css[j] === '{') depth++
      else if (css[j] === '}') depth--
      j++
    }
    const body = css.slice(braceIdx + 1, Math.max(braceIdx + 1, j - 1))
    const sel = prelude.trim()
    if (!sel) {
      i = j
      continue
    }
    if (sel.startsWith('@')) {
      const at = sel.toLowerCase()
      if (
        at.startsWith('@media') ||
        at.startsWith('@supports') ||
        at.startsWith('@layer') ||
        at.startsWith('@container')
      ) {
        out += `${sel}{${scopeEmailCss(body, scope)}}`
      } else {
        // @font-face, @keyframes, @page, vendor-prefixed — selector-
        // free (or selector-irrelevant) blocks; pass through.
        out += `${sel}{${body}}`
      }
    } else {
      const scoped = sel
        .split(',')
        .map((part) => {
          const s = part.trim()
          if (!s) return ''
          // `body`/`html` mean "the email canvas" — that's the scope
          // element now.
          const replaced = s.replace(/^(?:html\s+body|html|body)(?![\w-])/i, scope)
          return replaced !== s ? replaced : `${scope} ${s}`
        })
        .filter(Boolean)
        .join(', ')
      out += `${scoped}{${body}}`
    }
    i = j
  }
  return out
}

/** Rewrite every `<style>` element in the sanitised DOM so its rules
 *  are scoped under the email-body container. */
function scopeStyleElements(root: ParentNode): void {
  for (const styleEl of Array.from(root.querySelectorAll('style'))) {
    styleEl.textContent = scopeEmailCss(styleEl.textContent || '')
  }
}

/** Inline SVG placeholder for ``cid:`` refs that don't (yet) have a
 *  matching attachment row. Renders as a friendly grey 320×120 tile
 *  saying "Image syncing…" instead of the browser's native broken-
 *  image glyph. The placeholder is intentionally small (~600 bytes
 *  base64) so it doesn't bloat email DOMs that reference dozens of
 *  inline images. BUG-0014: operator complaint that inline images
 *  rendered as broken icons while the attachment download dispatcher
 *  was still catching up. Until the lazy-fetch-on-open work lands
 *  (Slice C, tracked separately), the placeholder makes the wait
 *  legible instead of looking like a render failure.
 *
 *  BUG-2026-05-21-IMAGE-PLACEHOLDER (Mark Newman screenshot — "What
 *  happened to the image here it is bugging out?"): the SVG itself
 *  was rendering, but the surrounding ``<img>`` was being stretched
 *  to the email's original ``width="900" height="600"`` (or similar)
 *  inline-attribute dimensions, which leaves a giant empty grey box
 *  with the "Image syncing…" text shrunk to a near-invisible 13pt
 *  inside a 600px-tall element. We now use ``preserveAspectRatio=
 *  "none"`` so the placeholder fills the entire stretched area, and
 *  re-size the text to 24pt so it stays legible at any container
 *  size (modern browsers render SVG text at the SVG's logical size,
 *  scaled by the ``<img>`` transform — at 24pt logical, a stretched
 *  600x300 render shows the text at ~60pt actual). */
const SYNCING_PLACEHOLDER_DATA_URL =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="120" viewBox="0 0 320 120" preserveAspectRatio="xMidYMid meet">' +
      '<rect width="320" height="120" rx="8" fill="#f3f4f6" stroke="#e5e7eb"/>' +
      '<g transform="translate(160,60)" fill="#9ca3af" text-anchor="middle" font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif">' +
      '<text y="-4" font-weight="600" font-size="16">Image syncing\u2026</text>' +
      '<text y="18" font-size="12" fill="#6b7280">Reload in a moment to see it</text>' +
      '</g>' +
      '</svg>',
  )

/** Strip width / height / style attributes that would inflate the
 *  placeholder ``<img>`` to the original image's full email-source
 *  dimensions. We keep the substituted SVG at its intrinsic 320×120
 *  so the "Image syncing…" text is always legible, instead of being
 *  invisible inside a stretched 900×600 grey rectangle (the symptom
 *  in BUG-2026-05-21-IMAGE-PLACEHOLDER).
 *
 *  Only called on the placeholder path — when the cid lookup
 *  SUCCEEDS we keep every original attribute so the real image
 *  renders at its intended dimensions. */
function stripDimensionAttrs(attrs: string): string {
  let out = attrs
  out = out.replace(/\s(?:width|height)\s*=\s*("[^"]*"|'[^']*'|\S+)/gi, '')
  // Drop width:/height:/min-/max- inside style="..." but keep the
  // rest of the style declaration intact (display, margin, etc.).
  out = out.replace(
    /\bstyle\s*=\s*("|')([^"']*)\1/gi,
    (_m, q: string, body: string) => {
      const filtered = body
        .split(';')
        .map((s) => s.trim())
        .filter((s) => {
          if (!s) return false
          const prop = s.split(':')[0]?.trim().toLowerCase() ?? ''
          return !(
            prop === 'width' ||
            prop === 'height' ||
            prop === 'min-width' ||
            prop === 'min-height' ||
            prop === 'max-width' ||
            prop === 'max-height'
          )
        })
        .join('; ')
      return filtered ? `style=${q}${filtered}${q}` : ''
    },
  )
  return out
}

/** Rewrite `<img src="cid:...">` references using the map. Refs that
 *  don't match an attachment used to be left in place so the browser
 *  showed a broken-icon glyph — the operator preferred that to
 *  "the email looks empty". BUG-0014: now we substitute a friendly
 *  data-URI placeholder ("Image syncing…") instead, which is the
 *  middle ground between a broken icon and an empty body: the user
 *  knows an image was there and that it's coming, without seeing a
 *  hard error. */
function rewriteCidReferences(
  rawHtml: string,
  cidMap: Map<string, string>,
): string {
  // Even when the cid map is empty we still want to rewrite — every
  // cid: ref in the body needs to become the syncing placeholder so
  // the user doesn't see a broken icon. Previously this returned
  // ``rawHtml`` unchanged in that case, which is what produced the
  // broken-icon screenshot in BUG-0014.
  return rawHtml.replace(
    CID_IMG_PATTERN,
    (_match, before: string, cidRaw: string, after: string) => {
      const url = cidMap.get(normaliseCid(cidRaw))
      if (url) {
        return `<img${before}src="${url}"${after}>`
      }
      // No matching attachment row yet — substitute the placeholder
      // and tag the <img> with a data attribute so a future "tap to
      // refresh" affordance (and CSS in the prose container) can key
      // off it specifically without affecting normal inline images.
      // ``alt`` doubles as the screen-reader description.
      //
      // BUG-2026-05-21-IMAGE-PLACEHOLDER: strip the original image's
      // width/height/dimension-styles so the placeholder renders at
      // its intrinsic 320×120 instead of a stretched 900×600 empty
      // grey box that hides the "Image syncing…" text below the fold.
      const cleanBefore = stripDimensionAttrs(before)
      const cleanAfter = stripDimensionAttrs(after)
      return (
        `<img${cleanBefore}` +
        `src="${SYNCING_PLACEHOLDER_DATA_URL}" ` +
        `data-cid-syncing="${normaliseCid(cidRaw)}" ` +
        `alt="Image syncing — reload to see it" ` +
        `style="display:inline-block;width:320px;max-width:100%;height:auto"` +
        `${cleanAfter}>`
      )
    },
  )
}

/**
 * Sanitize an email body HTML string.
 *
 * Pass `attachments` (the email's attachment array) to enable
 * `cid:` rewriting — every inline `<img src="cid:VALUE">` whose
 * Content-ID matches an attachment's `content_id` will be rewritten
 * to use the signed `inline_url` so the image actually renders.
 * Without `attachments` (or for cid: refs with no matching row),
 * the original src is preserved and the browser will show a broken
 * icon — better than dropping the image silently.
 *
 * Then runs DOMPurify with default config (drops `<script>`, event
 * handlers, dangerous URL schemes). Returns empty string for
 * nullish input.
 */
export function sanitizeEmailHtml(
  rawHtml: string | null | undefined,
  attachments?: ReadonlyArray<InlineAttachmentRef> | null,
): string {
  if (!rawHtml) return ''
  const cidMap = buildCidMap(attachments)
  const rewritten = unwrapWordStyleComments(rewriteCidReferences(rawHtml, cidMap))
  // DOMPurify's default allowlist permits ``data:image/svg+xml`` URLs
  // — we keep that on so the syncing placeholder SVG above renders.
  // It also preserves the ``data-cid-syncing`` attribute (data-* is
  // always allowlisted). If DOMPurify defaults ever tighten, the
  // placeholder will render as broken — pin both via explicit config
  // at that point.
  //
  // RETURN_DOM gives us the sanitised tree directly so the two
  // post-passes (img privacy/perf hints + <style> scoping) operate on
  // real elements instead of regexing the serialised string — exact,
  // idempotent, no markup-corruption edge cases.
  //
  // FORCE_BODY keeps a leading `<style>` (the `<head>` CSS of a full
  // HTML email document) in the output instead of silently dropping
  // it — safe now that scopeStyleElements rewrites every selector
  // under the body-scope attribute, and a fidelity WIN for marketing
  // emails whose layout CSS previously vanished.
  const dom = DOMPurify.sanitize(rewritten, {
    RETURN_DOM: true,
    FORCE_BODY: true,
  }) as HTMLElement
  hardenImageElements(dom)
  hardenAnchorElements(dom)
  scopeStyleElements(dom)
  return dom.innerHTML
}

/**
 * Sanitize generic user-supplied HTML (mention overlays,
 * formatted comments, markdown output, etc.). Same DOMPurify
 * defaults — no cid-strip because non-email content shouldn't
 * have those refs.
 */
export function sanitizeUserHtml(rawHtml: string | null | undefined): string {
  if (!rawHtml) return ''
  return DOMPurify.sanitize(rawHtml)
}
